import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AppLayout from "../../../navigation/Layout/Layout";
import ScrollTabs from "../../../Profile/components/ScrollTabs/ScrollTabs";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import useMedicalData from "@/Profile/hooks/useMedicalData";
import { selectEffectiveUserId } from "../../../sous-profile/redux/selectors/sousProfileSelectors";
import MetricCard from "@/Profile/components/OverViewsPage/MetricCard/MetricCard";
import BMIChart from "@/Profile/components/OverViewsPage/BMIChart/BMIChart";
import Allergies from "@/Profile/components/OverViewsPage/Allergies/Allergies";
import AllergiesStatus from "@/Profile/components/OverViewsPage/AllergiesStatus/AllergiesStatus";
import VaccinationHistory from "@/Profile/components/OverViewsPage/VaccinationHistory/VaccinationHistory";
import SurgicalHistory from "@/Profile/components/OverViewsPage/SurgicalHistory/SurgicalHistory";
import UpcomingMedicine from "@/Profile/components/OverViewsPage/UpcomingMedicine/UpcomingMedicine";
import OverviewTabsChart from "@/Profile/components/OverViewsPage/OverviewTabsChart/OverviewTabsChart";
import { selectVitalSignals } from "../../../Profile/redux/selectors/vitalSignsSelectors";
import MetricCarousel from "@/Profile/components/OverViewsPage/MetricCarousel/MetricCarousel";
import AddMetricModal from "@/Profile/components/OverViewsPage/AddMetricModal/AddMetricModal";

import {
  addVitalSignal,
  removeVitalSignal,
  fetchVitalSignals,
} from "@/Profile/redux/thunks/vitalThunks";

import {
  fetchBmiHistoryByUserId,
} from "@/Profile/redux/thunks/bmiHistoryThunks";

import HeartIcon from "../../../assets/icons/Icon-heart.png";
import BPIcon from "../../../assets/icons/Icon-Blood.png";
import RespiratoryIcon from "../../../assets/icons/Icon-Resperatory.png";

import "./Overviews.css";

const cleanVal = (val) => {
  if (
    val === null ||
    val === undefined ||
    String(val).trim() === ""
  ) {
    return undefined;
  }

  const normalized =
    typeof val === "string"
      ? val.replace(",", ".")
      : val;

  const n =
    parseFloat(normalized);

  return Number.isFinite(n)
    ? n
    : val;
};

const hasValue = (value) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== "";

const displayMetricValue = (...values) => {
  const value = values.find(
    (item) => item !== undefined && item !== null && String(item).trim() !== ""
  );
  return value ?? "--";
};

const dateOfMetric = (item) => {
  const timestamp =
    new Date(
      item?.measuredAt ||
        item?.updatedAt ||
        item?.createdAt ||
        item?.addedAt ||
        item?.date ||
        item?.timestamp ||
        0
    ).getTime();

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
};

const makeCustomKey = (label, unit) => {
  return `${String(label || "Métrique").trim()}__${String(unit || "").trim()}`;
};

const CUSTOM_TONES = [
  "low",
  "success",
  "danger",
  "warning",
  "purple",
  "pink",
  "orange",
  "teal",
  "neutral",
];

const getCustomTone = (index) => {
  return CUSTOM_TONES[index % CUSTOM_TONES.length];
};

const normalizeMetricLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/*
 * Empêche les anciennes métriques OTHER comme "tension", "pouls",
 * "temperature"... de créer une deuxième carte différente des vitaux
 * officiels BP_SYS / BP_DIA / HEART_RATE / TEMPERATURE / OXYGEN_SAT...
 */
const RESERVED_VITAL_LABELS = new Set([
  "glycemie",
  "glucose",
  "blood glucose",
  "tension",
  "tension arterielle",
  "blood pressure",
  "pression arterielle",
  "pouls",
  "frequence cardiaque",
  "heart rate",
  "frequence respiratoire",
  "respiratory rate",
  "temperature",
  "spo2",
  "saturation",
  "saturation oxygene",
  "oxygen saturation",
  "poids",
  "weight",
]);

const isReservedVitalCustomMetric = (metric) =>
  RESERVED_VITAL_LABELS.has(
    normalizeMetricLabel(metric?.customLabel)
  );

function getActiveKey(pathname, userId) {
  const p = String(pathname || "").toLowerCase();

  if (p.startsWith(ROUTES.OVERVIEWS.toLowerCase())) return ROUTES.OVERVIEWS;
  if (p.startsWith(ROUTES.MEDICAL_RECORD.toLowerCase())) return ROUTES.MEDICAL_RECORD;
  if (p.startsWith(ROUTES.MEDICINES.toLowerCase())) return ROUTES.MEDICINES;
  if (p.startsWith("/profile")) return ROUTES.PROFILE(userId || ":userId");

  return ROUTES.OVERVIEWS;
}

function SortableItem({ id, children, isEditMode, sizeClass }) {
  const contentRef = useRef(null);
  const [rowSpan, setRowSpan] = useState(1);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: !isEditMode,
  });

  useLayoutEffect(() => {
    const content = contentRef.current;
    const grid = content?.parentElement;
    if (!content || !grid) return undefined;

    const updateRowSpan = () => {
      const height = content.getBoundingClientRect().height;
      const gap = Number.parseFloat(
        window.getComputedStyle(grid).getPropertyValue("--ov-gap")
      ) || 0;
      setRowSpan(Math.max(1, Math.ceil(height + gap)));
    };

    updateRowSpan();
    const observer = new ResizeObserver(updateRowSpan);
    observer.observe(content);
    return () => observer.disconnect();
  }, [sizeClass]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isEditMode ? "grab" : "default",
    gridRowEnd: `span ${rowSpan}`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-item ${sizeClass || ""} ${
        isEditMode ? "draggable" : ""
      } ${isDragging ? "dragging" : ""}`}
      {...(isEditMode ? { ...attributes, ...listeners } : {})}
    >
      <div className="card-wrapper" ref={contentRef}>{children}</div>
    </div>
  );
}

export default function MedicalOverviewPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const userId =
    useSelector(selectEffectiveUserId) ||
    Cookies.get("userId") ||
    Cookies.get("id") ||
    "";

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeKey = getActiveKey(pathname, userId);

  const onTabChange = (key) => {
    navigate(key);
  };

  const vitalSignals = useSelector(selectVitalSignals);
  const [status, setStatus] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const showMessage = useCallback((type) => {
    setStatus(type);
    setTimeout(() => setStatus(""), 3000);
  }, []);

  /*
   * Recharge les vitaux + le BMI.
   * Utilisé au premier affichage et après une mesure ajoutée par le médecin.
   */
  const refreshMeasurements =
    useCallback(async () => {
      const cleanUserId =
        String(userId || "").trim();

      if (!cleanUserId) {
        return;
      }

      await Promise.allSettled([
        dispatch(
          fetchVitalSignals({
            userId:
              cleanUserId,
          })
        ).unwrap(),

        dispatch(
          fetchBmiHistoryByUserId(
            cleanUserId
          )
        ).unwrap(),
      ]);
    }, [
      dispatch,
      userId,
    ]);

  /*
   * Synchronisation des mesures :
   * - immédiatement dans la même fenêtre via CustomEvent
   * - autre onglet via storage
   * - autre appareil via polling léger toutes les 5 secondes
   */
  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    refreshMeasurements();

    const handleMedicalUpdate =
      (event) => {
        const updatedUserId =
          String(
            event?.detail?.userId ||
              ""
          ).trim();

        if (
          updatedUserId &&
          updatedUserId !==
            String(userId)
        ) {
          return;
        }

        refreshMeasurements();
      };

    const handleStorage =
      (event) => {
        if (
          event.key !==
          "patient-medical-measurements-updated"
        ) {
          return;
        }

        try {
          const payload =
            JSON.parse(
              event.newValue ||
                "{}"
            );

          if (
            payload?.userId &&
            String(
              payload.userId
            ) !==
              String(userId)
          ) {
            return;
          }
        } catch {
          // ignore invalid storage payload
        }

        refreshMeasurements();
      };

    const handleFocus =
      () => {
        refreshMeasurements();
      };

    window.addEventListener(
      "patient-medical-measurements-updated",
      handleMedicalUpdate
    );

    window.addEventListener(
      "storage",
      handleStorage
    );

    window.addEventListener(
      "focus",
      handleFocus
    );

    const intervalId =
      window.setInterval(
        () => {
          if (
            document.visibilityState ===
            "visible"
          ) {
            refreshMeasurements();
          }
        },
        5000
      );

    return () => {
      window.removeEventListener(
        "patient-medical-measurements-updated",
        handleMedicalUpdate
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );

      window.removeEventListener(
        "focus",
        handleFocus
      );

      window.clearInterval(
        intervalId
      );
    };
  }, [
    userId,
    refreshMeasurements,
  ]);

  /**
   * ✅ Custom metrics groupées par label + unit.
   * Exemple :
   * Sommeil 7h ancienne date
   * Sommeil 8h nouvelle date
   * => une seule card "Sommeil" avec value = 8
   * => history contient les deux valeurs pour le chart
   */
  const customMetrics = useMemo(() => {
    if (!Array.isArray(vitalSignals)) return [];

    const groups = new Map();

    vitalSignals
      .filter(
        (v) =>
          v?.type === "OTHER" &&
          !isReservedVitalCustomMetric(v)
      )
      .forEach((v) => {
        const label = v.customLabel || "Métrique";
        const unit = v.unit || "";
        const key = makeCustomKey(label, unit);

        if (!groups.has(key)) {
          groups.set(key, {
            key,
            id: `custom-${encodeURIComponent(key)}`,
            label,
            unit,
            history: [],
            sourceIds: [],
          });
        }

        const group = groups.get(key);
        group.history.push(v);
        group.sourceIds.push(v.id);
      });

    return Array.from(groups.values()).map((group, index) => {
      const sortedHistory = [...group.history].sort(
        (a, b) => dateOfMetric(b) - dateOfMetric(a)
      );

      const latest = sortedHistory[0];

      return {
        ...group,
        latestId: latest?.id,
        value: cleanVal(latest?.value) ?? latest?.value ?? "--",
        measuredAt: latest?.measuredAt || latest?.createdAt,
        history: sortedHistory,
        tone: getCustomTone(index),
        emoji: "💊",
      };
    });
  }, [vitalSignals]);

  const handleAddMetric = useCallback(
    async (data) => {
      if (!userId) return;

      const numericValue = Number(String(data.value).replace(",", ".")) || 0;

      try {
        await dispatch(
          addVitalSignal({
            userId,
            values: {
              type: "OTHER",
              value: numericValue,
              unit: data.unit?.trim() || undefined,
              customLabel: data.label,
              measuredAt: new Date().toISOString(),
            },
          })
        ).unwrap();

        await refreshMeasurements();
        showMessage("success");
      } catch (err) {
        console.error("Add custom metric error:", err);
        showMessage("error");
      }
    },
    [dispatch, userId, showMessage, refreshMeasurements]
  );

  const handleRemoveCustom = useCallback(
    async (cardId) => {
      if (!userId) return;

      const custom = customMetrics.find((m) => m.id === cardId);
      if (!custom) return;

      try {
        for (const id of custom.sourceIds) {
          await dispatch(removeVitalSignal({ userId, id })).unwrap();
        }

        await refreshMeasurements();
        showMessage("info");
      } catch (err) {
        console.error("Remove custom metric error:", err);
        showMessage("error");
      }
    },
    [dispatch, userId, customMetrics, showMessage, refreshMeasurements]
  );

  const latestVitals = useMemo(() => {
    if (!Array.isArray(vitalSignals)) {
      return {};
    }

    const byType = {};

    vitalSignals.forEach((signal) => {
      if (!signal?.type) {
        return;
      }

      const type =
        String(
          signal.type
        ).toUpperCase();

      if (
        !byType[type] ||
        dateOfMetric(signal) >
          dateOfMetric(
            byType[type]
          )
      ) {
        byType[type] =
          signal;
      }
    });

    const sys =
      cleanVal(
        byType.BP_SYS?.value
      );

    const dia =
      cleanVal(
        byType.BP_DIA?.value
      );

    let bloodPressure;

    if (
      hasValue(sys) &&
      hasValue(dia)
    ) {
      bloodPressure =
        `${sys}/${dia}`;
    } else if (
      hasValue(sys)
    ) {
      bloodPressure =
        String(sys);
    } else if (
      hasValue(dia)
    ) {
      bloodPressure =
        String(dia);
    }

    return {
      glucose:
        cleanVal(
          byType.GLYCEMIA?.value ??
            byType.BLOOD_GLUCOSE
              ?.value
        ),

      heartRate:
        cleanVal(
          byType.HEART_RATE?.value
        ),

      respiratoryRate:
        cleanVal(
          byType.RESP_RATE?.value ??
            byType.RESPIRATORY_RATE
              ?.value
        ),

      bloodPressure,

      temperature:
        cleanVal(
          byType.TEMPERATURE?.value
        ),

      oxygenSat:
        cleanVal(
          byType.OXYGEN_SAT?.value
        ),
    };
  }, [vitalSignals]);

  const {
    isEditing,
    saving,
    loading,
    error,
    metrics,
    allergies,
    vaccinations,
    surgeries,
    allergiesStatus,
    bmi,
    tempMetrics,
    tempAllergies,
    tempVaccinations,
    tempSurgeries,
    tempAllergiesStatus,
    setMetric,
    setTempAllergies,
    setTempVaccinations,
    setTempSurgeries,
    setTempAllergiesStatus,
    handleEditClick,
    handleCancelClick,
    handleSaveClick,
  } = useMedicalData(userId, { latestVitals });

  const [customMetricDrafts, setCustomMetricDrafts] = useState({});

  useEffect(() => {
    if (!isEditing) return;

    const initialDrafts = {};

    customMetrics.forEach((metric) => {
      initialDrafts[metric.id] =
        metric.value !== undefined && metric.value !== null
          ? String(metric.value)
          : "";
    });

    setCustomMetricDrafts(initialDrafts);
  }, [isEditing, customMetrics]);

  const NON_METRIC_INITIAL = [
    "bmi-chart",
    "allergies",
    "surgical-history",
    "upcoming-medicine",
    "overview-tabs-chart",
    "allergies-status",
    "vaccination-history",
  ];

  const BASE_METRIC_IDS = [
    "metric-gly",
    "metric-hr",
    "metric-bp",
    "metric-rr",
    "metric-temp",
    "metric-spo2",
  ];

  const [nonMetricCards, setNonMetricCards] = useState(NON_METRIC_INITIAL);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!userId) return;

    try {
      const saved = localStorage.getItem(`overview-layout-nm-${userId}`);

      if (saved) {
        const parsed = JSON.parse(saved);

        if (Array.isArray(parsed) && parsed.length) {
          setNonMetricCards(parsed);
        }
      }
    } catch (e) {
      console.error("Error loading layout:", e);
    }
  }, [userId]);

  const saveLayout = () => {
    if (!userId) return;
    localStorage.setItem(
      `overview-layout-nm-${userId}`,
      JSON.stringify(nonMetricCards)
    );
  };

  /*
   * Les 4 constantes principales restent sur la première page.
   * Les métriques personnalisées sont placées juste après,
   * avant Température et SpO₂, pour être visibles plus rapidement
   * dans le carousel après ajout par le médecin.
   */
  const allMetricIds = useMemo(
    () => [
      "metric-gly",
      "metric-hr",
      "metric-bp",
      "metric-rr",
      ...customMetrics.map((m) => m.id),
      "metric-temp",
      "metric-spo2",
    ],
    [customMetrics]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over || active.id === over.id) return;

    setNonMetricCards((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const toggleEditMode = (e) => {
    if (e) {
      e.preventDefault?.();
      e.stopPropagation?.();
    }

    if (isEditMode) {
      saveLayout();
      showMessage("success");
    }

    setIsEditMode((prev) => !prev);
    return false;
  };

  const getCardSize = (cardId) => {
    if (cardId === "bmi-chart") return "bmi-wide";
    if (cardId === "overview-tabs-chart") return "health-wide";
    if (cardId === "upcoming-medicine") return "medicine-tall";
    if (cardId === "allergies") return "medium";
    if (cardId === "surgical-history") return "medium";
    if (cardId === "allergies-status") return "small-card";
    if (cardId === "vaccination-history") return "medium";
    return "medium";
  };

  const saveCustomMetricChanges = useCallback(async () => {
    if (!userId) return;

    const changedMetrics = customMetrics.filter((metric) => {
      const draftValue = customMetricDrafts[metric.id];

      if (draftValue === undefined) return false;

      const oldValue = Number(String(metric.value).replace(",", "."));
      const newValue = Number(String(draftValue).replace(",", "."));

      if (!Number.isFinite(newValue)) return false;

      return oldValue !== newValue;
    });

    for (const metric of changedMetrics) {
      const newValue = Number(
        String(customMetricDrafts[metric.id]).replace(",", ".")
      );

      await dispatch(
        addVitalSignal({
          userId,
          values: {
            type: "OTHER",
            value: newValue,
            unit: metric.unit?.trim() || undefined,
            customLabel: metric.label,
            measuredAt: new Date().toISOString(),
          },
        })
      ).unwrap();
    }
  }, [dispatch, userId, customMetrics, customMetricDrafts]);

  const onSaveChanges = useCallback(
    (e) => {
      if (e) {
        e.preventDefault?.();
        e.stopPropagation?.();
        e.nativeEvent?.preventDefault?.();
        e.nativeEvent?.stopImmediatePropagation?.();
      }

      (async () => {
        try {
          /* Sauvegarde COMPLÈTE : allergies, antécédents chirurgicaux,
             vaccinations et mode de vie en plus des métriques. Avant, ce
             bouton n'envoyait que `onlyMetrics: true` : tout ce que le
             patient ajoutait dans ces cartes était ignoré (lecture seule
             de fait), puis écrasé au prochain fetch. */
          await handleSaveClick();

          await saveCustomMetricChanges();

          await refreshMeasurements();

          showMessage("success");
        } catch (err) {
          console.error("Save Changes error:", err);
          showMessage("error");
        }
      })();

      return false;
    },
    [
      handleSaveClick,
      saveCustomMetricChanges,
      refreshMeasurements,
      showMessage,
    ]
  );

  const onCancelChanges = useCallback(
    (e) => {
      if (e) {
        e.preventDefault?.();
        e.stopPropagation?.();
        e.nativeEvent?.preventDefault?.();
        e.nativeEvent?.stopImmediatePropagation?.();
      }

      handleCancelClick();
      setCustomMetricDrafts({});
      showMessage("info");

      return false;
    },
    [handleCancelClick, showMessage]
  );

  const resetLayout = useCallback(() => {
    setNonMetricCards(NON_METRIC_INITIAL);

    if (userId) {
      localStorage.removeItem(`overview-layout-nm-${userId}`);
    }

    showMessage("success");
  }, [userId, showMessage]);

  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  const renderCard = useCallback(
    (cardId) => {
      switch (cardId) {
        case "metric-gly":
          return (
            <MetricCard
              value={
                isEditing
                  ? tempMetrics.glucose
                  : displayMetricValue(latestVitals.glucose, metrics.glucose)
              }
              unit={t("medicalOverview.units.mgdl")}
              label={t(
                "medicalOverview.metrics.bloodGlucose",
                {
                  defaultValue:
                    "Glycémie",
                }
              )}
              tone="success"
              editable={isEditing}
              clickToEdit
              onEditStart={() => {
                if (!isEditing) handleEditClick();
              }}
              onEditEnd={() => {
                if (isEditing) onSaveChanges();
              }}
              onValueChange={(val) => setMetric("glucose", val)}
              inputPlaceholder="80-100"
              intervals={[
                {
                  min: undefined,
                  max: 69,
                  badgeLabel: t("medicalOverview.status.low"),
                },
                {
                  min: 70,
                  max: 99,
                  badgeLabel: t("medicalOverview.status.normal"),
                },
                {
                  min: 100,
                  max: 125,
                  badgeLabel: t("medicalOverview.status.abnormal"),
                },
                {
                  min: 126,
                  max: undefined,
                  badgeLabel: t("medicalOverview.status.high"),
                },
              ]}
            />
          );

        case "metric-hr":
          return (
            <MetricCard
              value={
                isEditing
                  ? tempMetrics.heartRate
                  : displayMetricValue(latestVitals.heartRate, metrics.heartRate)
              }
              unit={t("medicalOverview.units.bpm")}
              label={t(
                "medicalOverview.metrics.heartRate",
                {
                  defaultValue:
                    "Fréquence cardiaque",
                }
              )}
              tone="danger"
              iconsrc={HeartIcon}
              editable={isEditing}
              clickToEdit
              onEditStart={() => {
                if (!isEditing) handleEditClick();
              }}
              onEditEnd={() => {
                if (isEditing) onSaveChanges();
              }}
              onValueChange={(val) => setMetric("heartRate", val)}
              inputPlaceholder="60-100"
              intervals={[
                {
                  min: undefined,
                  max: 59,
                  badgeLabel: t("medicalOverview.status.low"),
                },
                {
                  min: 60,
                  max: 100,
                  badgeLabel: t("medicalOverview.status.normal"),
                },
                {
                  min: 101,
                  max: undefined,
                  badgeLabel: t("medicalOverview.status.high"),
                },
              ]}
            />
          );

        case "metric-bp":
          return (
            <MetricCard
              value={
                isEditing
                  ? tempMetrics.bloodPressure
                  : displayMetricValue(
                      latestVitals.bloodPressure,
                      metrics.bloodPressure
                    )
              }
              unit={t("medicalOverview.units.mmhg")}
              label={t(
                "medicalOverview.metrics.bloodPressure",
                {
                  defaultValue:
                    "Tension artérielle",
                }
              )}
              status={t("medicalOverview.status.normal")}
              tone="low"
              iconsrc={BPIcon}
              editable={isEditing}
              clickToEdit
              onEditStart={() => {
                if (!isEditing) handleEditClick();
              }}
              onEditEnd={() => {
                if (isEditing) onSaveChanges();
              }}
              onValueChange={(val) => setMetric("bloodPressure", val)}
              inputPlaceholder="120/80"
            />
          );

        case "metric-rr":
          return (
            <MetricCard
              value={
                isEditing
                  ? tempMetrics.respiratoryRate
                  : displayMetricValue(
                      latestVitals.respiratoryRate,
                      metrics.respiratoryRate
                    )
              }
              unit={t("medicalOverview.units.bpm")}
              label={t(
                "medicalOverview.metrics.respiratoryRate",
                {
                  defaultValue:
                    "Fréquence respiratoire",
                }
              )}
              tone="warning"
              iconsrc={RespiratoryIcon}
              editable={isEditing}
              clickToEdit
              onEditStart={() => {
                if (!isEditing) handleEditClick();
              }}
              onEditEnd={() => {
                if (isEditing) onSaveChanges();
              }}
              onValueChange={(val) => setMetric("respiratoryRate", val)}
              inputPlaceholder="12-20"
              intervals={[
                {
                  min: undefined,
                  max: 11,
                  badgeLabel: t("medicalOverview.status.low"),
                },
                {
                  min: 12,
                  max: 20,
                  badgeLabel: t("medicalOverview.status.normal"),
                },
                {
                  min: 21,
                  max: undefined,
                  badgeLabel: t("medicalOverview.status.high"),
                },
              ]}
            />
          );


        case "metric-temp":
          return (
            <MetricCard
              value={
                displayMetricValue(
                  latestVitals.temperature
                )
              }
              unit="°C"
              label={t(
                "medicalOverview.metrics.temperature",
                {
                  defaultValue:
                    "Température",
                }
              )}
              tone="warning"
              editable={false}
              intervals={[
                {
                  min: undefined,
                  max: 35.9,
                  badgeLabel: t(
                    "medicalOverview.status.low",
                    {
                      defaultValue:
                        "Low",
                    }
                  ),
                },
                {
                  min: 36,
                  max: 37.5,
                  badgeLabel: t(
                    "medicalOverview.status.normal",
                    {
                      defaultValue:
                        "Normal",
                    }
                  ),
                },
                {
                  min: 37.6,
                  max: undefined,
                  badgeLabel: t(
                    "medicalOverview.status.high",
                    {
                      defaultValue:
                        "High",
                    }
                  ),
                },
              ]}
            />
          );

        case "metric-spo2":
          return (
            <MetricCard
              value={
                displayMetricValue(
                  latestVitals.oxygenSat
                )
              }
              unit="%"
              label={t(
                "medicalOverview.metrics.oxygenSaturation",
                {
                  defaultValue:
                    "SpO₂",
                }
              )}
              tone="success"
              editable={false}
              intervals={[
                {
                  min: undefined,
                  max: 94,
                  badgeLabel: t(
                    "medicalOverview.status.low",
                    {
                      defaultValue:
                        "Low",
                    }
                  ),
                },
                {
                  min: 95,
                  max: 100,
                  badgeLabel: t(
                    "medicalOverview.status.normal",
                    {
                      defaultValue:
                        "Normal",
                    }
                  ),
                },
              ]}
            />
          );

        default:
          if (cardId.startsWith("custom-")) {
            const custom = customMetrics.find((m) => m.id === cardId);

            if (!custom) return null;

            return (
              <MetricCard
                value={
                  isEditing
                    ? customMetricDrafts[custom.id] ?? custom.value
                    : custom.value
                }
                unit={custom.unit}
                label={custom.label}
                status="Custom"
                tone={custom.tone}
                emoji={custom.emoji}
                editable={isEditing}
                onValueChange={(val) => {
                  setCustomMetricDrafts((prev) => ({
                    ...prev,
                    [custom.id]: val,
                  }));
                }}
                inputPlaceholder="0"
              />
            );
          }

          switch (cardId) {
            case "bmi-chart":
              return (
                <BMIChart
                  bmiData={null}
                  currentBMI={bmi}
                  loading={false}
                  error={null}
                  height={isEditing ? tempMetrics.height : metrics.height}
                  weight={isEditing ? tempMetrics.weight : metrics.weight}
                  editable={isEditing}
                  tempHeight={tempMetrics.height}
                  tempWeight={tempMetrics.weight}
                  onHeightChange={(val) => setMetric("height", val)}
                  onWeightChange={(val) => setMetric("weight", val)}
                  onSave={onSaveChanges}
                  onCancel={onCancelChanges}
                  saving={saving}
                />
              );

            case "allergies":
              return (
                <Allergies
                  editable={isEditing}
                  initialAllergies={isEditing ? tempAllergies : allergies}
                  onAllergiesChange={setTempAllergies}
                />
              );

            case "surgical-history":
              return (
                <SurgicalHistory
                  editable={isEditing}
                  items={isEditing ? tempSurgeries : surgeries}
                  variant="overview"
                  onChange={setTempSurgeries}
                />
              );

            case "upcoming-medicine":
              return <UpcomingMedicine />;

            case "overview-tabs-chart":
              return <OverviewTabsChart />;

            case "allergies-status":
              return (
                <AllergiesStatus
                  value={isEditing ? tempAllergiesStatus : allergiesStatus}
                  editable={isEditing}
                  onChange={setTempAllergiesStatus}
                />
              );

            case "vaccination-history":
              return (
                <VaccinationHistory
                  editable={isEditing}
                  initialItems={isEditing ? tempVaccinations : vaccinations}
                  onChange={setTempVaccinations}
                />
              );

            default:
              return null;
          }
      }
    },
    [
      isEditing,
      tempMetrics,
      latestVitals,
      metrics,
      bmi,
      allergies,
      vaccinations,
      surgeries,
      allergiesStatus,
      tempAllergies,
      tempVaccinations,
      tempSurgeries,
      tempAllergiesStatus,
      setMetric,
      setTempAllergies,
      setTempVaccinations,
      setTempSurgeries,
      setTempAllergiesStatus,
      onSaveChanges,
      onCancelChanges,
      saving,
      customMetrics,
      customMetricDrafts,
      t,
    ]
  );

  if (loading) {
    return (
      <div className="medical-overview-loading">
        <div className="spinner"></div>
        <p>{t("medicalOverview.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="profile medical-overview-page">
          <ScrollTabs activeKey={activeKey} onChange={onTabChange} userId={userId} />

          <div className="medical-overview-error-container">
            <div className="medical-overview-error-content">
              <div className="error-icon">⚠️</div>

              <h2 className="error-title">
                {t("medicalOverview.errorTitle", "Erreur de chargement")}
              </h2>

              <p className="error-message">
                {t("medicalOverview.error", {
                  error: error || "Erreur inconnue",
                })}
              </p>

              <p className="error-suggestion">
                {t(
                  "medicalOverview.errorSuggestion",
                  "Veuillez essayer de rafraîchir la page ou réinitialiser la mise en page."
                )}
              </p>

              <div className="error-actions">
                <button className="btn-primary" onClick={refreshPage}>
                  🔄 {t("medicalOverview.actions.refresh", "Rafraîchir")}
                </button>

                <button className="btn-secondary" onClick={resetLayout}>
                  ↺{" "}
                  {t(
                    "medicalOverview.actions.resetLayout",
                    "Réinitialiser la mise en page"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="profile medical-overview-page">
        <ScrollTabs activeKey={activeKey} onChange={onTabChange} userId={userId} />

        <div className="ov-grid">
          <div className={`ov-content ${isEditMode ? "edit-mode" : ""}`}>
            <div className="ov-top-bar">
              <div className="ov-title-block">
                <h2 className="ov-title">{t("medicalOverview.title")}</h2>
                <p className="ov-subtitle">{t("medicalOverview.subtitle")}</p>
              </div>

              <div className="ov-header-actions">
                <button
                  className="ph-btn--primary"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleEditMode(e);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  title={
                    isEditMode
                      ? t("medicalOverview.actions.saveLayout")
                      : t("medicalOverview.actions.modifyLayout")
                  }
                >
                  {isEditMode
                    ? t("medicalOverview.actions.saveLayout")
                    : t("medicalOverview.actions.modifyOverview")}
                </button>

                {!isEditing && !isEditMode && (
                  <button
                    className="ph-btn--primary"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditClick();
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {t("medicalOverview.actions.editValues")}
                  </button>
                )}

                {isEditing && (
                  <>
                    <button
                      className="btn-secondary"
                      type="button"
                      disabled={saving}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancelChanges(e);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {t("medicalOverview.actions.cancel")}
                    </button>

                    <button
                      className="btn-primary"
                      type="button"
                      disabled={saving}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSaveChanges(e);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {saving
                        ? t("medicalOverview.actions.saving")
                        : t("medicalOverview.actions.saveChanges")}
                    </button>
                  </>
                )}
              </div>
            </div>

            <MetricCarousel
              cards={allMetricIds}
              renderCard={renderCard}
              onAddDirect={handleAddMetric}
              isEditMode={isEditMode}
              customCards={customMetrics}
              onRemoveCustom={handleRemoveCustom}
            />

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={nonMetricCards} strategy={rectSortingStrategy}>
                <div className="ov-flexible-grid">
                  {nonMetricCards.map((cardId) => (
                    <SortableItem
                      key={cardId}
                      id={cardId}
                      isEditMode={isEditMode}
                      sizeClass={`card-${getCardSize(cardId)}`}
                    >
                      {renderCard(cardId)}
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>

              <DragOverlay>
                {activeId ? (
                  <div className={`drag-overlay-card card-${getCardSize(activeId)}`}>
                    <div className="card-wrapper">{renderCard(activeId)}</div>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      </div>

      <AddMetricModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={handleAddMetric}
      />

      {status && (
        <div className={`message-overlay ${status}`}>
          {status === "success"
            ? t("medicalOverview.messages.success")
            : status === "error"
            ? t("medicalOverview.messages.error")
            : t("medicalOverview.messages.cancelled")}
        </div>
      )}
    </AppLayout>
  );
}